import { fetchSoraVideoContent } from '@/client/api/sora';
import type { StoryboardSegmentPreviewEntry, StoryboardPreviewDownloadSource } from '../types';
import { getMediaRecord, saveMediaRecord, type StoryboardMediaRecordInput } from '../storage';
import type { StoryboardMediaRecord } from '../types';
import { guessSoraVideoIdFromPreviewEntry } from './soraHelpers';

const MIME_EXTENSION_MAP: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'video/x-matroska': 'mkv',
  'video/ogg': 'ogv',
};

const sanitizeFileName = (value: string): string =>
  value
    .trim()
    .replace(/[\p{Cc}<>:"/\\|?*]+/gu, '_')
    .replace(/\s+/g, '_');

type DownloadSourceFetchResult =
  | {
      kind: 'blob';
      blob: Blob;
      mimeType?: string;
      fileName?: string;
      originalUrl?: string;
    }
  | {
      kind: 'remote';
      url: string;
      originalUrl?: string;
    };

const fetchFromDownloadSource = async (
  entry: StoryboardSegmentPreviewEntry
): Promise<DownloadSourceFetchResult | null> => {
  const source = entry.downloadSource;
  if (!source) {
    return null;
  }

  switch (source.type) {
    case 'sora': {
      const result = await fetchSoraVideoContent(source.videoId);
      if (result.blob) {
        const blob = result.blob;
        const mimeType = blob.type || result.blob.type || undefined;
        const preferredBaseName = source.fileName
          ? sanitizeFileName(source.fileName)
          : sanitizeFileName(`sora_${source.videoId}`);
        const extension = guessMediaFileExtension(mimeType);
        const resolvedFileName = result.filename
          ? sanitizeFileName(result.filename)
          : `${preferredBaseName}.${extension}`;
        return {
          kind: 'blob',
          blob,
          mimeType,
          fileName: resolvedFileName,
          originalUrl: result.videoUrl,
        };
      }
      if (result.videoUrl) {
        return {
          kind: 'remote',
          url: result.videoUrl,
          originalUrl: result.videoUrl,
        };
      }
      return null;
    }
    default:
      return null;
  }
};

export const guessMediaFileExtension = (mimeType?: string): string => {
  if (!mimeType) return 'bin';
  const normalized = mimeType.toLowerCase();
  if (MIME_EXTENSION_MAP[normalized]) {
    return MIME_EXTENSION_MAP[normalized];
  }
  if (normalized.startsWith('video/')) {
    return normalized.split('/')[1]?.toLowerCase() ?? 'video';
  }
  return 'bin';
};

const inFlightFetches = new Map<string, Promise<StoryboardMediaRecord | null>>();

const isCacheableUrl = (value?: string | null): boolean => {
  if (!value) return false;
  try {
    const parsed = new URL(value, window.location.href);
    if (parsed.protocol === 'blob:' || parsed.protocol === 'data:') {
      return false;
    }
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};

export const ensureMediaRecordForPreview = async (options: {
  projectId: string;
  segmentId: string;
  entry: StoryboardSegmentPreviewEntry;
  signal?: AbortSignal;
}): Promise<StoryboardMediaRecord | null> => {
  if (typeof window === 'undefined') return null;
  const { projectId, segmentId, entry, signal } = options;
  if (!entry?.previewId || !projectId || !segmentId) {
    return null;
  }
  const existing = await getMediaRecord(entry.previewId);
  if (existing) {
    return existing;
  }
  let downloadSource: StoryboardPreviewDownloadSource | null = entry.downloadSource ?? null;
  if (!downloadSource) {
    const guessedVideoId = guessSoraVideoIdFromPreviewEntry(entry);
    if (guessedVideoId) {
      downloadSource = { type: 'sora', videoId: guessedVideoId };
    }
  }
  const entryWithDownloadSource = downloadSource ? { ...entry, downloadSource } : entry;
  const hasCacheableRemoteUrl = entry.url ? isCacheableUrl(entry.url) : false;
  if (!downloadSource && !hasCacheableRemoteUrl) {
    return null;
  }
  const inFlight = inFlightFetches.get(entry.previewId);
  if (inFlight) {
    return inFlight;
  }
  const fetchPromise = (async () => {
    try {
      let recordInput: StoryboardMediaRecordInput | null = null;
      let fallbackUrl: string | undefined;

      if (downloadSource) {
        try {
          const sourceResult = await fetchFromDownloadSource(entryWithDownloadSource);
          if (sourceResult?.kind === 'blob') {
            const blob = sourceResult.blob;
            const mimeType = sourceResult.mimeType ?? blob.type ?? undefined;
            const fileName =
              sourceResult.fileName ??
              `${sanitizeFileName(entry.label ?? `preview_${entry.previewId}`)}.${guessMediaFileExtension(mimeType)}`;
            recordInput = {
              previewId: entry.previewId,
              projectId,
              segmentId,
              blob,
              mimeType,
              originalUrl: sourceResult.originalUrl ?? entry.url,
              fileName,
            };
          } else if (sourceResult?.kind === 'remote') {
            fallbackUrl = sourceResult.url;
          }
        } catch (error) {
          if ((error as Error)?.name !== 'AbortError') {
            console.warn('[storyboard] 通过下载源缓存视频失败', error);
          }
        }
      }

      const targetUrl = recordInput
        ? null
        : entry.url && hasCacheableRemoteUrl
          ? entry.url
          : fallbackUrl && isCacheableUrl(fallbackUrl)
            ? fallbackUrl
            : null;

      if (!recordInput && targetUrl) {
        const response = await fetch(targetUrl, {
          signal,
        });
        if (!response.ok) {
          console.warn('[storyboard] 缓存视频失败，返回状态异常', response.status, targetUrl);
          return null;
        }
        const blob = await response.blob();
        const mimeType = response.headers.get('content-type') ?? blob.type ?? undefined;
        let fileName: string | undefined;
        if (entry.label) {
          fileName = sanitizeFileName(entry.label);
        }
        if (!fileName) {
          fileName = `preview_${entry.previewId}`;
        }
        const extension = guessMediaFileExtension(mimeType);
        recordInput = {
          previewId: entry.previewId,
          projectId,
          segmentId,
          blob,
          mimeType,
          originalUrl: targetUrl,
          fileName: `${fileName}.${extension}`,
        };
      }

      if (!recordInput && fallbackUrl && !isCacheableUrl(fallbackUrl)) {
        try {
          const response = await fetch(fallbackUrl, {
            signal,
          });
          if (response.ok) {
            const blob = await response.blob();
            const mimeType = response.headers.get('content-type') ?? blob.type ?? undefined;
            let fileName: string | undefined;
            if (entry.label) {
              fileName = sanitizeFileName(entry.label);
            }
            if (!fileName) {
              fileName = `preview_${entry.previewId}`;
            }
            const extension = guessMediaFileExtension(mimeType);
            recordInput = {
              previewId: entry.previewId,
              projectId,
              segmentId,
              blob,
              mimeType,
              originalUrl: fallbackUrl,
              fileName: `${fileName}.${extension}`,
            };
          }
        } catch (error) {
          if ((error as Error)?.name !== 'AbortError') {
            console.warn('[storyboard] 尝试从远程地址缓存视频失败', error);
          }
        }
      }

      if (!recordInput) {
        return null;
      }

      return saveMediaRecord(recordInput);
    } catch (error) {
      if ((error as Error)?.name !== 'AbortError') {
        console.warn('[storyboard] 缓存视频失败，已回退至远程地址', error);
      }
      return null;
    }
  })();
  inFlightFetches.set(entry.previewId, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    inFlightFetches.delete(entry.previewId);
  }
};

export const getMediaObjectUrl = async (
  previewId: string
): Promise<{ record: StoryboardMediaRecord; objectUrl: string } | null> => {
  if (!previewId || typeof window === 'undefined') return null;
  const record = await getMediaRecord(previewId);
  if (!record) return null;
  const objectUrl = URL.createObjectURL(record.blob);
  return { record, objectUrl };
};
